import React, { useContext, useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import { apiEndPoints } from '../../../common/api.endPoints';
import { DocumentType } from '../../../common/constants/documentType';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { HttpService } from '../../../common/http.service';
import { SnackBarType } from '../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../layout/SnackbarContext';
import { ITask } from '../../tasks/interfaces/ITask';
import {
  AutoMedicalClaims,
  AutoMedicalClaimsData,
} from '../../tasks/types/autoMedicalClaims';
import './components/autoMedicalClaims/AutoMedicalClaims.scss';
import { AutoMedicalClaimsReasonSection } from './components/autoMedicalClaims/AutoMedicalClaimsReasonSection';
import { AutoMedicalClaimsRuleHitsPanel } from './components/autoMedicalClaims/AutoMedicalClaimsRuleHitsPanel';
import { fetchAutoMedicalClaims } from './components/autoMedicalClaims/fetchAutoMedicalClaims';
import { MedicalClaim, RuleHit } from './components/autoMedicalClaims/types';
import { RetryableEvidenceItem } from './components/RetryableEvidenceItem';

export const AutoMedicalClaimsDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  retryHandler,
  markOutdatedHandler,
  deleteHandler,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  retryHandler?: (taskId: number) => void;
  markOutdatedHandler: (taskId: number) => void;
  showRunButton: boolean;
}) => {
  const [loadingMagicLink, setLoadingMagicLink] = useState(false);
  const [ruleHits, setRuleHits] = useState<RuleHit[]>([]);
  const [claimsById, setClaimsById] = useState<Record<number, MedicalClaim>>(
    {},
  );
  const [independentClaims, setIndependentClaims] = useState<MedicalClaim[]>(
    [],
  );
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const data: AutoMedicalClaims = task.taskData as AutoMedicalClaims;
  const facts: AutoMedicalClaimsData = task.evidence
    ?.facts as AutoMedicalClaimsData;
  const errorMessage = task.evidence?.errorMessage;

  const evidenceReasons: string[] = (data?.reason || []).map((item: string) =>
    ReasonsTransform.getReasonDisplay(item),
  );
  const hasHit = facts?.recordExists ?? false;
  const hasClear = facts?.recordClear ?? false;

  const showError = (message: string) => {
    setSnacks([
      ...openSnacks,
      {
        id: uuidv4(),
        type: SnackBarType.ALERT,
        message,
      },
    ]);
  };

  const evidenceId = task.evidenceId;

  useEffect(() => {
    if (!evidenceId || errorMessage || !(hasHit || hasClear)) return;

    let cancelled = false;
    fetchAutoMedicalClaims(evidenceId)
      .then(result => {
        if (cancelled) return;
        setRuleHits(result.ruleHits);
        setClaimsById(result.claimsById);
        setIndependentClaims(result.independentClaims);
      })
      .catch(() => {
        if (!cancelled) {
          showError('Failed to load auto medical claims rule data');
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceId, hasHit, hasClear, errorMessage]);

  const onOpenMillimanPortal = async () => {
    setLoadingMagicLink(true);
    try {
      const response = await HttpService.getInstance().get(
        `${apiEndPoints.millimanPortal}?taskId=${task.id}`,
      );
      const url = await response?.text();
      if (url?.length > 0) {
        window.open(url, '_blank');
      } else {
        showError('Can not open milliman portal link, please try again later');
      }
    } catch (error) {
      console.error(error);
      showError('Can not open milliman portal link, please try again later');
    } finally {
      setLoadingMagicLink(false);
    }
  };

  return (
    <Box data-label={ElementLocators.LOCATOR_AUTO_MEDICAL_CLAIMS_SECTION}>
      <RetryableEvidenceItem
        task={task}
        documentType={DocumentType.AUTO_MEDICAL_CLAIMS}
        title={Resource.TASK_TITLE_AUTO_MEDICAL_CLAIMS}
        downloadHandler={downloadHandler}
        uploadHandler={uploadHandler}
        deleteHandler={deleteHandler}
        retryHandler={retryHandler}
        taskStateHandler={taskStateHandler}
        markOutdatedHandler={markOutdatedHandler}
        renderChildrenWhenFilesExist={true}
      >
        {!errorMessage && (
          <AutoMedicalClaimsRuleHitsPanel
            ruleHits={ruleHits}
            claimsById={claimsById}
            independentClaims={independentClaims}
            hasHit={hasHit}
            hasClear={hasClear}
            loadingMagicLink={loadingMagicLink}
            onOpenMillimanPortal={onOpenMillimanPortal}
          />
        )}

        <AutoMedicalClaimsReasonSection
          evidenceReasons={evidenceReasons}
          reasonDescription={data?.reasonDescription}
        />
      </RetryableEvidenceItem>
    </Box>
  );
};
